/** The multi-buy's dated window as the page tells it, and when the page reads
 *  itself again so that a page left open does not go on showing an offer
 *  that has ended, or miss one that has begun.
 *
 *  The window is the server's (seed prices.multiBuy.windows, multiBuyNow and
 *  datedMultiBuy in functions/src/lib/templates.ts): the Christmas, Black
 *  Friday and Cyber Week offer, 13 November to 2 December 2026, every extra
 *  piece −35% instead of the usual −25%. The page decides nothing about it.
 *  It only says what the server sent, with its end date and "usually", never
 *  a struck price, and re-reads the page when the server's answer would
 *  change.
 *
 *  Plain TypeScript the way Node can load it by stripping the types (no
 *  imports, no enums), like multiBuy.ts: check-templates runs these on the
 *  server's own page data at pinned dates. */

/** What the page is sent about the multi-buy (PriceOffer.multiBuy). */
export interface MultiBuyIn {
  percent: number;
  usual?: number;
  endsOn?: string;
  until?: string;
  frame?: "christmas" | "late";
}

/** A dated window, as the page says it: the percentage every extra piece
 *  takes off now, the one it usually takes (null for a lead whose own code
 *  already takes as much, FIRSTORDER30, for whom "usually −25%" would not be
 *  true), the last day in Lisbon ("2026-12-02", written long by the page),
 *  the instant it ends, and how it is told: "christmas" up to the last day to
 *  order for Christmas, "late" (Black Friday and Cyber Week) after, and null
 *  from a server that did not say, which the page tells with the standing
 *  title. */
export interface DatedOffer {
  percent: number;
  usual: number | null;
  endsOn: string;
  until: number;
  frame: "christmas" | "late" | null;
}

/** The window the page was sent, or null outside one: then the page says
 *  the multi-buy exactly as it always has, with no end date. Not checked
 *  against the clock: a page left open past the end keeps the window's words
 *  until it has read itself again (rereadDelay below), rather than saying the
 *  window's percentage for a moment as if it had no end. The server refuses
 *  a payment priced at it either way. */
export function datedOffer(multiBuy: MultiBuyIn | null | undefined): DatedOffer | null {
  if (!multiBuy || !multiBuy.endsOn || !multiBuy.until) return null;
  const until = Date.parse(multiBuy.until);
  if (!Number.isFinite(until) || !/^\d{4}-\d{2}-\d{2}$/.test(multiBuy.endsOn)) return null;
  const usual = multiBuy.usual;
  return {
    percent: multiBuy.percent,
    usual: typeof usual === "number" && usual > 0 && usual < multiBuy.percent ? usual : null,
    endsOn: multiBuy.endsOn,
    until,
    frame: multiBuy.frame === "christmas" || multiBuy.frame === "late" ? multiBuy.frame : null,
  };
}

/** The last day of a window as an instant formatDate() writes as that day in
 *  Lisbon: midday UTC, which is the same day in Lisbon all year. */
export function endsOnInstant(endsOn: string): string {
  return `${endsOn}T12:00:00Z`;
}

const DAY_MS = 86_400_000;

/** The next midnight in Lisbon after `now`, in milliseconds. Every answer of
 *  the server that moves with the calendar moves then: a window that starts or
 *  ends at midnight (the real one does, 13 November and 3 December at 00:00
 *  Lisbon), its framing (Christmas to Black Friday on 20 November) and the
 *  delivery dates under the price. On the two nights a year the clocks change
 *  it is an hour early or late, which only moves a re-read. */
export function nextLisbonMidnight(now: number): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Lisbon",
    hourCycle: "h23",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(now));
  const part = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0) || 0;
  const since = ((part("hour") * 60 + part("minute")) * 60 + part("second")) * 1000 + (now % 1000);
  return now - since + DAY_MS;
}

/** A moment after the instant itself, so the server's clock is past it too
 *  when the read arrives. */
export const REREAD_AFTER_MS = 3000;

/** How soon a page still holding a window that has ended by this device's
 *  clock asks again: the last read failed, or this clock is ahead of the
 *  server's. */
export const STALE_REREAD_MS = 30_000;

/** For how long after a window's end by this device's clock the page keeps
 *  asking again that soon. Past it, the device's clock is taken to be ahead
 *  of the server's (which still sends the window), and the page waits for
 *  midnight like any other: a clock set a day ahead would otherwise read the
 *  page every 30 seconds for that whole day. The checkout refuses a stale
 *  total either way. */
export const STALE_GIVE_UP_MS = 10 * 60_000;

/** How long from `now` until the page should read itself again: at the end
 *  of every window it was sent (`until`, which an owner's test window can put
 *  at any minute), or at the next midnight in Lisbon, whichever comes first,
 *  plus REREAD_AFTER_MS; and soon (STALE_REREAD_MS) for a window that has
 *  ended in the last STALE_GIVE_UP_MS. Always within a day, so the browser's
 *  timer can hold it. */
export function rereadDelay(untils: (string | undefined | null)[], now: number): number {
  let at = nextLisbonMidnight(now);
  for (const until of untils) {
    const end = until ? Date.parse(until) : NaN;
    if (!Number.isFinite(end)) continue;
    if (end <= now) {
      if (now - end < STALE_GIVE_UP_MS) return STALE_REREAD_MS;
      continue;
    }
    if (end < at) at = end;
  }
  return at - now + REREAD_AFTER_MS;
}
